/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
/* Optional CSS utils that can be commented out */
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
/* Theme variables */
import './theme/fonts.css';
import './theme/global.css';
import './theme/variables.css';
import './theme/modal.css';
import './theme/custom-select.css';
import './i18n.config';

import { Suspense, useEffect, useRef, useState } from 'react';

import { IonContent, IonPage, IonSpinner, setupIonicReact } from '@ionic/react';

import { StatusBar, Style } from '@capacitor/status-bar';
import { BLE } from '@ionic-native/ble';
import { Storage } from '@ionic/storage';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { observer } from 'mobx-react-lite';
import firebaseConfig from './config/firebase.json';

import { AndroidNotch } from '@awesome-cordova-plugins/android-notch';
import { IonReactRouter } from '@ionic/react-router';
import DeepLinkObserver from 'DeepLinkObserver';
import { Device } from 'util/device';
import {
  Environment,
  RootStore,
  RootStoreProvider,
  setupRootStore,
} from './models';
import { DebugStore, useDebugStore } from './models/DebugStore/DebugStore';
import Navigation from './Navigation';
import {
  AuthProvider,
  IAuthService,
} from './services/Authentication/AuthService';
import DummyAuth from './services/Authentication/DummyAuth';
import FirebaseEmulatorAuth from './services/Authentication/emulator';
import FirebaseAuth from './services/Authentication/FirebaseAuth';
import { BluetoothProvider } from './services/BLE';
import BluetoothLEService from './services/BLE/BLE';
import BLEMock from './services/BLE/DummyBluetooth';
import {
  APIProvider,
  EstimatorInterface,
} from './services/freak_estimator/Estimator';
import DummyServer from './services/freak_estimator/DummyAPI';
import OnlineAPI from './services/freak_estimator/OnlineAPI';
import {
  ICloudStorage,
  StorageProvider,
} from './services/Database/CloudStorageService';
import DummyStorage from './services/Database/Dummy';
import FirebaseCloudStorage from './services/Database/FirebaseCloudStorage';
import FirebaseEmulatorStorage from './services/Database/FirebaseCloudStorage/emulator';
import {
  GeoLocationProvider,
  IGeoLocationService,
} from './services/GeoLocation';
import DummyGeoLocator from './services/GeoLocation/DummyGeoLocator';
import W3CGeoLocatorIOS from './services/GeoLocation/W3CGeoLocatorIOS';
import W3CGeoLocatorAndroid from './services/GeoLocation/W3CGeoLocatorAndroid';
import { FSMProvider, FSMService } from './services/DongleController/DongleController';
import { getConsoleMethods, setupLogging } from './util';

import { App as CapacitorApp } from '@capacitor/app';
import { isInDebugMode } from './util';
import * as Sentry from '@sentry/capacitor';
import * as SentrySibling from '@sentry/react';
import { Breadcrumb, BreadcrumbHint } from '@sentry/react';
import { safeJoin } from '@sentry/utils';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { configure, toJS } from 'mobx';
import { logger, LogLevel } from 'services/Logger';

const IS_DBG = isInDebugMode();
// make some stuff available to the debugger
if (IS_DBG)
  _.merge(window, {
    toJS,
  });

// enable Snetry in production apps for now.
if (!IS_DBG) {
  CapacitorApp.getInfo().then((info) => {
    Sentry.init(
      {
        dsn: 'https://5041d10eda0246caa95455572aebd76d@o4504912127983616.ingest.sentry.io/4504922130612224',
        // To set your release and dist versions
        release: `spinnax-mobile-app@${info.version}_${info.build}`,
        environment: IS_DBG ? 'Develop' : 'Production',
        dist: '1',
        debug: IS_DBG,
        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
        // We recommend adjusting this value in production.
        tracesSampleRate: 10.0,
        beforeBreadcrumb: (breadcrumb: Breadcrumb, _hint?: BreadcrumbHint) => {
          // `loggingClass` adds some color to the output. Sentry doesn't like that.
          if (breadcrumb.category === 'console' && breadcrumb.data?.arguments) {
            if ((breadcrumb.data!.arguments[0] as string).startsWith('%c')) {
              const newArgs = breadcrumb
                .data!.arguments.filter(
                  (arg: any) =>
                    typeof arg !== 'string' || !arg.startsWith('color:')
                )
                .map((arg: any) => {
                  if (typeof arg === 'string') {
                    return arg.replaceAll('%c', '');
                  }
                  return arg;
                });
              const message = safeJoin(newArgs, ' ');
              return {
                ...breadcrumb,
                data: {
                  ...breadcrumb.data,
                  arguments: newArgs,
                },
                message,
              };
            }
          }
          return breadcrumb;
        },
      },
      // Forward the init method to the sibling Framework.
      SentrySibling.init
    );
  });
}

const originalConsoleMethods = getConsoleMethods();
setupIonicReact();
setupLogging(originalConsoleMethods);

export const LoadingFallback: React.FC<{}> = () => {
  return (
    <IonPage className="notchSafe">
      <IonContent style={{ height: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
        </div>
      </IonContent>
    </IonPage>
  );
};

interface AppProps {
  geo: IGeoLocationService;
  firebaseAuth: IAuthService;
  cloudStorage: ICloudStorage;
  api: EstimatorInterface;
  ble: typeof BLE;
  env: Environment;
  debugStore: DebugStore;
}

const FullApp: React.FC<AppProps> = ({
  geo,
  firebaseAuth,
  cloudStorage,
  api,
  ble,
  env,
  debugStore,
}) => {
  const [rootStore, setRootStore] = useState<RootStore | undefined>(undefined);
  const [blue, setBlue] = useState<BluetoothLEService | undefined>();
  const fsm = useRef<FSMService | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  logger.setLogLevel(LogLevel.ERROR);
  logger.setLogLevel(LogLevel.ERROR);

  useEffect(() => {
    setupRootStore(env).then((rs) => {
      // Debug: Make store available on window for testing
      if (isInDebugMode()) {
        (window as any).rootStore = rs;
      }

      // this has nasty side-effects we don't want to trigger every time.
      const bluetooth = new BluetoothLEService(ble);
      setBlue(bluetooth);

      const _fsm = new FSMService(bluetooth, rs.knownFreak, debugStore);
      fsm.current = _fsm;

      if (_.has(ble, 'fsm')) (ble as unknown as typeof BLEMock).fsm = _fsm;

      setRootStore(rs);
      setIsLoading(false);
    });

    return () => {
      fsm.current?.destroy();
      blue?.destroy();
    };
  }, []);
  // TODO: show Splash-screen?
  if (isLoading || !(rootStore && blue)) return <LoadingFallback />;

  return (
    <Suspense fallback={<LoadingFallback />}>
      <RootStoreProvider value={rootStore}>
        <AuthProvider value={firebaseAuth}>
          <StorageProvider value={cloudStorage}>
            <APIProvider value={api}>
              <BluetoothProvider value={blue}>
                <GeoLocationProvider value={geo}>
                  <FSMProvider value={fsm.current!}>
                    <IonReactRouter>
                      <DeepLinkObserver />
                      <Navigation />
                    </IonReactRouter>
                  </FSMProvider>
                </GeoLocationProvider>
              </BluetoothProvider>
            </APIProvider>
          </StorageProvider>
        </AuthProvider>
      </RootStoreProvider>
    </Suspense>
  );
};

const App: React.FC = observer(() => {
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();
  // setup debugStore
  const debugStore = useDebugStore();

  const geo = useRef<IGeoLocationService | null>(null);
  const firebaseAuth = useRef<IAuthService | null>(null);
  const cloudStorage = useRef<ICloudStorage | null>(null);
  const api = useRef<EstimatorInterface | null>(null);

  // Web app body class effect
  useEffect(() => {
    if (process.env.REACT_APP_IS_WEB === 'true') {
      document.body.classList.add('web-app');
    } else {
      document.body.classList.remove('web-app');
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    Device.setup().then(async () => {
      await i18n.changeLanguage(
        process.env.NODE_ENV === 'production' ? Device.languageCode : 'cimode'
      );
      
      // setup Statusbar color and safe-areas for android
      if (Device.platform !== 'web') {
        StatusBar.setStyle({ style: Style.Light });
        if (Device.platform === 'android') {
          const style = document.documentElement.style;
          AndroidNotch.getInsetTop().then((px) => {
            style.setProperty('--ion-safe-area-top', `${px}px`);
          });
          AndroidNotch.getInsetBottom().then((px) => {
            style.setProperty('--ion-safe-area-bottom', `${px}px`);
          });
          StatusBar.setOverlaysWebView({ overlay: true });
        }
      }

      // GeoLocation Service
      const isWeb = Device.platform === 'web' || (typeof window !== 'undefined' && !window.cordova);
      
      let _geo: IGeoLocationService;
      
      if (isWeb || debugStore.location === 'Dummy') {
        _geo = new DummyGeoLocator();
      } else if (Device.platform === 'ios') {
        _geo = new W3CGeoLocatorIOS();
      } else if (Device.platform === 'android') {
        _geo = new W3CGeoLocatorAndroid();
      } else {
        // Fallback for unknown platforms
        _geo = new W3CGeoLocatorIOS(); // iOS als Default

      }
      
      geo.current = _geo;

      let app: FirebaseApp;
      if (debugStore.auth === 'Firebase' || debugStore.storage === 'Firebase') {
        app = initializeApp(firebaseConfig);
      }

      // Authentication Service
      const _firebaseAuth: IAuthService =
        debugStore.auth === 'Dummy'
          ? new DummyAuth()
          : new (debugStore.useFirebaseEmulator
              ? FirebaseEmulatorAuth
              : FirebaseAuth)(app!);
      firebaseAuth.current = _firebaseAuth;

      // CloudStorage Service
      const _cloudStorage: ICloudStorage =
        debugStore.storage === 'Dummy'
          ? new DummyStorage()
          : debugStore.useFirebaseEmulator
            ? new FirebaseEmulatorStorage(
                app!,
                _firebaseAuth,
                debugStore.trickStore
              )
            : new FirebaseCloudStorage(
                app!,
                _firebaseAuth,
                debugStore.trickStore
              );
      cloudStorage.current = _cloudStorage;

      // TODO do we still need this as extra class?
      const _api: EstimatorInterface =
        debugStore.api === 'Dummy'
          ? new DummyServer(_cloudStorage)
          : new OnlineAPI(_cloudStorage, debugStore.trickStore);
      api.current = _api;

      setIsLoading(false);
    });

    return () => {
      geo.current?.destroy();
      firebaseAuth.current?.destroy();
      cloudStorage.current?.destroy();
      api.current?.destroy();
    };
  }, [debugStore, i18n]);

  if (
    isLoading ||
    !geo.current ||
    !firebaseAuth.current ||
    !cloudStorage.current ||
    !api.current
  ) {
    return <LoadingFallback />;
  }

  const localStorage = new Storage();
  localStorage.create();

  const env = new Environment(api.current, cloudStorage.current, localStorage);

  // Before we show the app, we have to wait for our state to be ready.
  // In the meantime, don't render anything. This will be the background
  // color set in native by rootView's background color.
  // In iOS: application:didFinishLaunchingWithOptions:
  // In Android: https://stackoverflow.com/a/45838109/204044
  // You can replace with your own loading component if you wish.

  const isWeb = Device.platform === 'web' || (typeof window !== 'undefined' && !window.cordova);
  const ble = isWeb ? BLEMock : debugStore.bluetooth === 'BLE' ? BLE : BLEMock;

  configure({ enforceActions: 'always' });

  return (
    <FullApp
      geo={geo.current}
      firebaseAuth={firebaseAuth.current}
      cloudStorage={cloudStorage.current}
      api={api.current}
      ble={ble}
      env={env}
      debugStore={debugStore}
    />
  );
});

export default App;
